import {
  Check,
  FloppyDisk,
  Sparkle,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { useAuthedUser } from "./AuthGate";

/**
 * WorkbookPickForm — slim bottom submit-bar for workbook picks.
 *
 * 2026-05-23 cycle-5c+25-fix7 (per Owner correction):
 *   Per-question picks (radios + free-text) are now rendered INLINE in the
 *   workbook article under each question via `WorkbookInlineQuestionPick`.
 *   This component is the slim FOOTER that collects:
 *     - "Accept all MP recommendations" toggle (top)
 *     - Cross-question owner notes textarea
 *     - Preview / Send buttons
 *     - Submit-success surface (replaces the form once committed)
 *
 * All state lives in `useWorkbookPickState` and is passed in via the `state`
 * prop. This component is fully controlled (no internal useState).
 */
export default function WorkbookPickForm({ state }) {
  const { user } = useAuthedUser();
  const {
    questions,
    hasMpRecs,
    acceptAll,
    setAcceptAll,
    notes,
    setNotes,
    submitting,
    submitResult,
    showPreview,
    preview,
    allAnswered,
    onPreview,
    onCommit,
  } = state;

  // Submit-success surface — replaces the whole form once committed.
  if (submitResult && submitResult.pushed) {
    return (
      <div
        className="surface-card p-5 mt-4"
        style={{
          borderColor: "hsl(var(--status-success) / 0.5)",
          background: "hsl(var(--status-success) / 0.05)",
        }}
        data-testid="mp-pick-submit-success"
      >
        <div className="flex items-start gap-3">
          <Check
            size={22}
            weight="bold"
            style={{ color: "hsl(var(--status-success))" }}
          />
          <div className="flex-1">
            <h3
              className="text-base font-semibold"
              style={{ color: "hsl(var(--foreground))" }}
            >
              Picks sent to MP for review
            </h3>
            <p
              className="text-xs mt-1"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              Your picks are logged as an Owner input record. MP will arbitrate at next
              active cycle (typically same day) and author a canonical verdict file. No
              canonical decision binds yet.
            </p>
            <p
              className="text-xs mt-2"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              Owner ack file:{" "}
              <code className="text-mono" style={{ fontSize: "0.8em" }}>
                {submitResult.target_path}
              </code>
            </p>
            <p
              className="text-xs mt-0.5"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              Commit SHA:{" "}
              <code className="text-mono" style={{ fontSize: "0.8em" }}>
                {submitResult.commit_sha?.slice(0, 12)}
              </code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-card p-3 sm:p-5 mt-4" data-testid="mp-pick-form">
      <div className="flex items-center gap-2 mb-1">
        <FloppyDisk size={16} style={{ color: "hsl(var(--primary))" }} />
        <h3
          className="text-base font-semibold"
          style={{ color: "hsl(var(--foreground))" }}
        >
          Send picks to MP for review
        </h3>
      </div>
      <p
        className="text-xs mb-4"
        style={{ color: "hsl(var(--muted-foreground))" }}
      >
        Answer each question inline above (letter <em>or</em> free-text, or both).{" "}
        <strong>
          This submission is sent to MP for arbitration in a separate response cycle
        </strong>{" "}
        — MP will read your picks (including free-text), weigh cross-impact against canon,
        then author an arbitration verdict file at next active cycle. Submitting writes an
        Owner ack file to{" "}
        <code className="text-mono" style={{ fontSize: "0.85em" }}>
          MP_INBOX/
        </code>{" "}
        as an Owner input record (committed as{" "}
        <code className="text-mono" style={{ fontSize: "0.85em" }}>
          {user.email}
        </code>
        ); it does NOT bind any canonical decision on its own. The canonical decision
        binds when MP's arbitration ack lands at a separate path.
      </p>

      {hasMpRecs && (
        <div
          className="rounded-md border p-3 mb-4 flex items-start gap-2"
          style={{
            borderColor: "hsl(var(--primary) / 0.3)",
            background: "hsl(var(--primary) / 0.05)",
          }}
        >
          <input
            id="accept-all"
            type="checkbox"
            checked={acceptAll}
            onChange={(e) => setAcceptAll(e.target.checked)}
            className="mt-0.5"
            data-testid="mp-pick-accept-all"
          />
          <label
            htmlFor="accept-all"
            className="text-sm cursor-pointer"
            style={{ color: "hsl(var(--foreground))" }}
          >
            <Sparkle
              size={12}
              weight="fill"
              className="inline mr-1"
              style={{ color: "hsl(var(--primary))" }}
            />
            Accept all MP recommendations (clears any inline picks above)
          </label>
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="owner-notes" className="eyebrow block mb-1">
          Cross-question notes for MP (optional)
        </label>
        <textarea
          id="owner-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="e.g. 'Defer Q4 to next cycle' or 'See you in 2 weeks'"
          className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          style={{
            borderColor: "hsl(var(--border))",
            background: "hsl(var(--background))",
            color: "hsl(var(--foreground))",
          }}
          data-testid="mp-pick-notes"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        <button
          type="button"
          onClick={onPreview}
          disabled={!allAnswered || submitting}
          className="inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 sm:py-1.5 text-xs transition-colors disabled:opacity-50 w-full sm:w-auto"
          style={{
            borderColor: "hsl(var(--border))",
            color: "hsl(var(--foreground))",
          }}
          data-testid="mp-pick-preview-btn"
        >
          <ArrowSquareOut size={11} />
          Preview ack file
        </button>
        <button
          type="button"
          onClick={onCommit}
          disabled={!allAnswered || submitting}
          className="inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 sm:py-1.5 text-xs font-medium transition-colors disabled:opacity-50 w-full sm:w-auto"
          style={{
            background: "hsl(var(--primary))",
            color: "hsl(var(--primary-foreground))",
          }}
          data-testid="mp-pick-commit-btn"
        >
          <FloppyDisk size={11} weight="fill" />
          {submitting ? "Sending\u2026" : "Send picks to MP for review"}
        </button>
        {!allAnswered && !acceptAll && (
          <span
            className="text-xs"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            Answer all {questions.length} questions (inline above), or check "Accept all"
          </span>
        )}
      </div>

      {showPreview && preview && (
        <div
          className="mt-4 rounded-md border p-3"
          style={{
            borderColor: "hsl(var(--border))",
            background: "hsl(var(--muted))",
          }}
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
            <span className="eyebrow">Preview — will be written to:</span>
            <code
              className="text-mono break-all"
              style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
            >
              {preview.target_path}
            </code>
          </div>
          <pre
            className="text-mono whitespace-pre-wrap"
            style={{
              fontSize: "0.75rem",
              color: "hsl(var(--foreground))",
              maxHeight: "260px",
              overflowY: "auto",
            }}
          >
            {preview.preview}
          </pre>
        </div>
      )}
    </div>
  );
}
